/* eslint-disable complexity, max-lines, max-lines-per-function */
import StopIcon from '@mui/icons-material/Stop';
import MicNoneIcon from '@mui/icons-material/MicNone';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Modal,
  Snackbar,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

const VoiceRecordingContext = React.createContext(false);
const RECORDING_WARMUP_MS = 1_000;
const RECORDING_TIMESLICE_MS = 1_000;
const MAX_RECORDING_BYTES = 24 * 1024 * 1024;
const MAX_RECORDING_DURATION_MS = 20 * 60 * 1_000;
const WAVEFORM_BAR_HEIGHTS = [8, 16, 24, 14, 20, 10, 18, 12, 22, 8];
const VOICE_BUTTON_GRADIENT =
  'linear-gradient(110deg, #69C4CD 0%, #4F9EA4 32%, #42636B 62%, #1A3641 100%)';
const VOICE_BUTTON_SHADOW = '0 3px 8px rgba(26, 54, 65, 0.32)';

const RecordingControl: React.FC<{
  mobile?: boolean;
  onStop: () => void;
  stopLabel: string;
}> = ({ mobile = false, onStop, stopLabel }) => (
  <Box
    sx={{
      display: 'inline-flex',
      flexDirection: mobile ? 'column' : 'row',
      alignItems: 'stretch',
      minWidth: mobile ? 200 : 140,
      height: mobile ? 112 : 32,
      overflow: 'hidden',
      borderRadius: mobile ? '12px' : '4px',
      color: '#fff',
      background: VOICE_BUTTON_GRADIENT,
      boxShadow: VOICE_BUTTON_SHADOW,
      '@keyframes voiceWaveform': {
        from: { transform: 'scaleY(0.35)', opacity: 0.65 },
        to: { transform: 'scaleY(1)', opacity: 1 },
      },
    }}
  >
    <Box
      aria-hidden="true"
      data-testid="voice-recording-waveform"
      sx={{
        display: 'flex',
        flex: 1,
        minHeight: mobile ? 48 : undefined,
        alignItems: 'center',
        justifyContent: 'center',
        gap: '3px',
        px: 1.5,
      }}
    >
      {WAVEFORM_BAR_HEIGHTS.map((height, index) => (
        <Box
          key={`${height}-${index}`}
          sx={{
            width: 2,
            height,
            borderRadius: 1,
            bgcolor: 'currentColor',
            transformOrigin: 'center',
            animation: `voiceWaveform 0.8s ease-in-out ${
              index * -0.09
            }s infinite alternate`,
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none',
            },
          }}
        />
      ))}
    </Box>
    <Tooltip title={stopLabel}>
      <IconButton
        aria-label={stopLabel}
        color="inherit"
        onClick={onStop}
        size="small"
        sx={{
          width: mobile ? '100%' : 36,
          minHeight: mobile ? 64 : undefined,
          borderLeft: mobile ? 0 : '1px solid rgba(255, 255, 255, 0.35)',
          borderTop: mobile ? '1px solid rgba(255, 255, 255, 0.35)' : 0,
          borderRadius: 0,
          '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.14)' },
        }}
      >
        <StopIcon sx={{ fontSize: mobile ? 36 : 20 }} />
      </IconButton>
    </Tooltip>
  </Box>
);

export const VoiceRecordingProvider: React.FC<{
  children: React.ReactNode;
  enabled: boolean;
}> = ({ children, enabled }) => (
  <VoiceRecordingContext.Provider value={enabled}>
    {children}
  </VoiceRecordingContext.Provider>
);

export const useVoiceRecordingEnabled = (): boolean =>
  React.useContext(VoiceRecordingContext);

const getApiDomain = (): string =>
  window.location.hostname === 'localhost' ? 'http://localhost:3000' : '';

const getCsrfToken = (): string | undefined =>
  document.cookie
    .split(';')
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith('XSRF-TOKEN='))
    ?.split('=')
    .slice(1)
    .join('=');

const getRecordingOptions = (): MediaRecorderOptions => {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/ogg;codecs=opus',
    'audio/mp4',
  ];
  const mimeType = candidates.find((type) =>
    MediaRecorder.isTypeSupported(type),
  );
  return mimeType ? { mimeType } : {};
};

const extensionForMimeType = (mimeType: string): string => {
  if (mimeType.includes('ogg')) return 'ogg';
  if (mimeType.includes('mp4')) return 'mp4';
  return 'webm';
};

type RecoverableRecording = {
  blob: Blob;
  durationSeconds: number;
};

export interface VoiceRecordingButtonProps {
  disabled?: boolean;
  onTranscript: (text: string, options: VoiceTranscriptOptions) => void;
}

export interface VoiceTranscriptOptions {
  focusInput: boolean;
}

export const VoiceRecordingButton: React.FC<VoiceRecordingButtonProps> = ({
  disabled,
  onTranscript,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const enabled = useVoiceRecordingEnabled();
  const available = enabled && !disabled;
  const [starting, setStarting] = React.useState(false);
  const [recording, setRecording] = React.useState(false);
  const [transcribing, setTranscribing] = React.useState(false);
  const [error, setError] = React.useState<string>();
  const [recoverableRecording, setRecoverableRecording] =
    React.useState<RecoverableRecording>();
  const recorderRef = React.useRef<MediaRecorder>();
  const streamRef = React.useRef<MediaStream>();
  const chunksRef = React.useRef<Blob[]>([]);
  const recordedBytesRef = React.useRef(0);
  const abortRef = React.useRef<AbortController>();
  const warmupTimerRef = React.useRef<number>();
  const durationTimerRef = React.useRef<number>();
  const recordingStartedAtRef = React.useRef<number>();
  const mountedRef = React.useRef(true);
  const availableRef = React.useRef(available);
  const isMobileRef = React.useRef(isMobile);
  const onTranscriptRef = React.useRef(onTranscript);
  React.useLayoutEffect(() => {
    availableRef.current = available;
  }, [available]);
  React.useEffect(() => {
    onTranscriptRef.current = onTranscript;
  }, [onTranscript]);
  React.useEffect(() => {
    isMobileRef.current = isMobile;
  }, [isMobile]);

  const dismissMobileKeyboard = React.useCallback(() => {
    if (!isMobileRef.current) return;
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, []);

  const stopTracks = React.useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = undefined;
  }, []);

  const cancelActiveWork = React.useCallback(
    (resetUi: boolean) => {
      window.clearTimeout(warmupTimerRef.current);
      window.clearTimeout(durationTimerRef.current);
      abortRef.current?.abort();
      abortRef.current = undefined;
      const recorder = recorderRef.current;
      recorderRef.current = undefined;
      if (recorder && recorder.state !== 'inactive') {
        recorder.ondataavailable = null;
        recorder.onstop = null;
        recorder.stop();
      }
      chunksRef.current = [];
      recordedBytesRef.current = 0;
      recordingStartedAtRef.current = undefined;
      stopTracks();
      if (resetUi && mountedRef.current) {
        setStarting(false);
        setRecording(false);
        setTranscribing(false);
      }
    },
    [stopTracks],
  );

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      cancelActiveWork(false);
    };
  }, [cancelActiveWork]);

  React.useEffect(() => {
    if (!available) cancelActiveWork(true);
  }, [available, cancelActiveWork]);

  const uploadRecording = React.useCallback(
    async (blob: Blob, durationSeconds: number) => {
      if (!availableRef.current) return;
      setRecoverableRecording({ blob, durationSeconds });
      setTranscribing(true);
      const abortController = new AbortController();
      abortRef.current = abortController;
      try {
        const form = new FormData();
        const mimeType = blob.type.split(';')[0] || 'audio/webm';
        form.append(
          'file',
          blob,
          `recording.${extensionForMimeType(mimeType)}`,
        );
        form.append('durationSeconds', String(durationSeconds));
        const csrfToken = getCsrfToken();
        const accessToken = window.localStorage.getItem('token');
        const response = await fetch(
          `${getApiDomain()}/api/voice-transcription`,
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              ...(accessToken
                ? { Authorization: `Bearer ${accessToken}` }
                : {}),
              ...(csrfToken
                ? { 'X-XSRF-TOKEN': decodeURIComponent(csrfToken) }
                : {}),
            },
            body: form,
            signal: abortController.signal,
          },
        );
        if (!response.ok) {
          const errorBody = (await response.json().catch(() => undefined)) as
            | { message?: string | string[] }
            | undefined;
          const message = Array.isArray(errorBody?.message)
            ? errorBody.message.join(', ')
            : errorBody?.message;
          throw new Error(
            message || `Transcription failed (${response.status})`,
          );
        }
        const result = (await response.json()) as { text?: string };
        const transcript = result.text?.trim();
        if (!transcript) {
          setRecoverableRecording(undefined);
          return;
        }
        if (mountedRef.current && availableRef.current) {
          onTranscriptRef.current(transcript, {
            focusInput: !isMobileRef.current,
          });
          setRecoverableRecording(undefined);
        }
      } catch (uploadError) {
        if (
          mountedRef.current &&
          availableRef.current &&
          !abortController.signal.aborted
        )
          setError(
            uploadError instanceof Error
              ? uploadError.message
              : 'Transcription failed',
          );
      } finally {
        if (abortRef.current === abortController) {
          abortRef.current = undefined;
          if (mountedRef.current) setTranscribing(false);
        }
      }
    },
    [dismissMobileKeyboard],
  );

  const toggleRecording = React.useCallback(async () => {
    if (!availableRef.current) return;
    setError(undefined);
    const currentRecorder = recorderRef.current;
    if (currentRecorder?.state === 'recording') {
      currentRecorder.stop();
      setRecording(false);
      return;
    }
    if (starting || transcribing) return;

    dismissMobileKeyboard();
    setStarting(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!mountedRef.current || !availableRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        if (mountedRef.current) setStarting(false);
        return;
      }
      const recorder = new MediaRecorder(stream, getRecordingOptions());
      chunksRef.current = [];
      recordedBytesRef.current = 0;
      streamRef.current = stream;
      recorderRef.current = recorder;
      recorder.onstart = () => {
        recordingStartedAtRef.current = performance.now();
        durationTimerRef.current = window.setTimeout(() => {
          if (recorder.state !== 'recording') return;
          if (mountedRef.current && availableRef.current) {
            setError('Recording stopped at the maximum duration');
          }
          recorder.stop();
        }, MAX_RECORDING_DURATION_MS);
        warmupTimerRef.current = window.setTimeout(() => {
          if (
            !mountedRef.current ||
            !availableRef.current ||
            recorder.state !== 'recording'
          )
            return;
          setStarting(false);
          setRecording(true);
        }, RECORDING_WARMUP_MS);
      };
      recorder.ondataavailable = ({ data }) => {
        if (!data.size || !availableRef.current) return;
        if (recordedBytesRef.current + data.size > MAX_RECORDING_BYTES) {
          if (mountedRef.current) {
            setError('Recording stopped before reaching the upload limit');
          }
          if (recorder.state === 'recording') recorder.stop();
          return;
        }
        chunksRef.current.push(data);
        recordedBytesRef.current += data.size;
      };
      recorder.onstop = () => {
        window.clearTimeout(warmupTimerRef.current);
        window.clearTimeout(durationTimerRef.current);
        recorderRef.current = undefined;
        const durationSeconds = recordingStartedAtRef.current
          ? Math.max(
              0.02,
              (performance.now() - recordingStartedAtRef.current) / 1_000,
            )
          : 0.02;
        recordingStartedAtRef.current = undefined;
        const blob = new Blob(chunksRef.current, {
          type: recorder.mimeType || 'audio/webm',
        });
        chunksRef.current = [];
        recordedBytesRef.current = 0;
        stopTracks();
        if (mountedRef.current) {
          setStarting(false);
          setRecording(false);
        }
        if (!blob.size || !availableRef.current) return;
        if (blob.size > MAX_RECORDING_BYTES) {
          setRecoverableRecording(undefined);
          setError('Recording is too large to upload');
          return;
        }
        void uploadRecording(blob, durationSeconds);
      };
      recorder.start(RECORDING_TIMESLICE_MS);
    } catch (recordingError) {
      stopTracks();
      setStarting(false);
      if (availableRef.current) {
        setError(
          recordingError instanceof Error
            ? recordingError.message
            : 'Microphone access failed',
        );
      }
    }
  }, [
    dismissMobileKeyboard,
    starting,
    stopTracks,
    transcribing,
    uploadRecording,
  ]);

  const retryRecording = React.useCallback(() => {
    if (!recoverableRecording || !availableRef.current) return;
    setError(undefined);
    void uploadRecording(
      recoverableRecording.blob,
      recoverableRecording.durationSeconds,
    );
  }, [recoverableRecording, uploadRecording]);

  const dismissError = React.useCallback(() => {
    setError(undefined);
    setRecoverableRecording(undefined);
  }, []);

  if (!enabled || typeof MediaRecorder === 'undefined') return null;

  const title = error
    ? error
    : starting
      ? t('voiceRecording.starting')
      : recording
        ? t('voiceRecording.stop')
        : transcribing
          ? t('voiceRecording.transcribing')
          : t('voiceRecording.start');

  return (
    <>
      <Box sx={{ display: 'inline-flex', ml: 'auto' }}>
        {recording && !isMobile ? (
          <RecordingControl
            onStop={() => void toggleRecording()}
            stopLabel={t('voiceRecording.stop')}
          />
        ) : (
          !recording && (
            <Tooltip title={title}>
              <span style={{ display: 'inline-flex' }}>
                <Button
                  aria-label={title}
                  disabled={disabled || starting || transcribing}
                  onPointerDown={(event) => {
                    if (
                      event.button !== 0 ||
                      starting ||
                      transcribing ||
                      disabled
                    )
                      return;
                    event.preventDefault();
                    event.stopPropagation();
                    dismissMobileKeyboard();
                    void toggleRecording();
                  }}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    // Pointer clicks are handled earlier on pointer-down. A zero detail
                    // identifies keyboard and assistive-technology activation.
                    if (event.detail === 0) void toggleRecording();
                  }}
                  onTouchStart={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    dismissMobileKeyboard();
                  }}
                  size="small"
                  type="button"
                  startIcon={
                    transcribing || starting ? (
                      <CircularProgress color="inherit" size={18} />
                    ) : (
                      <MicNoneIcon fontSize="small" />
                    )
                  }
                  sx={{
                    minWidth: 112,
                    border: 0,
                    borderRadius: '4px',
                    px: 2,
                    color: '#fff',
                    fontWeight: 600,
                    textTransform: 'none',
                    background: VOICE_BUTTON_GRADIENT,
                    boxShadow: VOICE_BUTTON_SHADOW,
                    '&:hover': {
                      background: VOICE_BUTTON_GRADIENT,
                      filter: 'brightness(0.92)',
                    },
                    '&.Mui-disabled': {
                      color: 'rgba(255, 255, 255, 0.75)',
                      background: VOICE_BUTTON_GRADIENT,
                      opacity: 0.6,
                    },
                  }}
                >
                  {t('voiceRecording.assistant')}
                </Button>
              </span>
            </Tooltip>
          )
        )}
      </Box>
      {isMobile ? (
        <Modal
          aria-label={t('voiceRecording.stop')}
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
          open={recording}
          slotProps={{
            backdrop: {
              'data-testid': 'voice-recording-backdrop',
              sx: {
                backdropFilter: 'blur(4px)',
                WebkitBackdropFilter: 'blur(4px)',
                backgroundColor: 'rgba(0, 0, 0, 0.15)',
              },
            },
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              outline: 'none',
            }}
          >
            <RecordingControl
              mobile
              onStop={() => void toggleRecording()}
              stopLabel={t('voiceRecording.stop')}
            />
          </Box>
        </Modal>
      ) : null}
      <Snackbar
        open={Boolean(error)}
        autoHideDuration={recoverableRecording ? null : 8000}
        onClose={dismissError}
      >
        <Alert
          severity="error"
          onClose={dismissError}
          action={
            recoverableRecording ? (
              <Button
                color="inherit"
                disabled={!available || transcribing}
                onClick={retryRecording}
                size="small"
              >
                {t('voiceRecording.retry')}
              </Button>
            ) : undefined
          }
        >
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};
